import React from 'react';
import { Link } from 'react-router-dom';
import { Modal } from './ui/Modal';
import { Users, Play, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { StoryLikeButton } from './StoryLikeButton';
import { SoundEmbed } from './SoundEmbed';
import type { Story, Profile, Band } from '../types/database';
import { dateLocales } from '../i18n';

interface StoryModalProps {
  story: Story & {
    author: Profile;
    band: Band;
    likes_count?: number;
  };
  isOpen: boolean;
  onClose: () => void;
}

export function StoryModal({ story, isOpen, onClose }: StoryModalProps) {
  const { t, i18n } = useTranslation();
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;
  
  // Determine media type from URL
  const getMediaType = (url: string) => {
    if (!url) return null;
    
    // Check for Spotify/SoundCloud
    if (url.includes('spotify.com') || url.includes('soundcloud.com')) {
      return 'audio';
    }
    
    // Check for video extensions or video platforms
    if (url.match(/\.(mp4|webm|ogg|mov|avi)$/i) || 
        url.includes('youtube.com') || 
        url.includes('youtu.be') || 
        url.includes('vimeo.com')) {
      return 'video';
    }
    
    // Check for image extensions
    if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
      return 'image';
    }
    
    // Default to external link
    return 'external';
  };
  
  const mediaType = story.image_url ? getMediaType(story.image_url) : null;
  
  const renderMediaContent = () => {
    if (!story.image_url || !mediaType) return null;
    
    switch (mediaType) {
      case 'image':
        return (
          <div className="mt-8">
            <img
              src={story.image_url}
              alt={story.title}
              className="w-full h-auto rounded-t-xl"
             loading="lazy" />
          </div>
        );
        
      case 'video':
        return (
          <div className="mt-8 relative">
            <video
              src={story.image_url}
              controls
              className="w-full h-auto rounded-t-xl"
              poster=""
            >
              Your browser does not support the video tag.
            </video>
          </div>
        );
        
      case 'audio':
        // Create a temporary sound object for the SoundEmbed component
        const soundObject = {
          id: story.id,
          band_id: story.band_id,
          title: story.title,
          url: story.image_url,
          provider: story.image_url.includes('spotify.com') ? 'spotify' : 'soundcloud',
          created_at: story.created_at,
          updated_at: story.updated_at,
          order: null
        };
        
        return (
          <div className="mt-8">
            <div className="bg-gray-100 p-4 rounded-t-xl">
              <SoundEmbed sound={soundObject} />
            </div>
          </div>
        );
        
      case 'external':
        // For external URLs that might be images, try to display them as images
        return (
          <div className="mt-8">
            <img
              src={story.image_url}
              alt={story.title}
              className="w-full h-auto rounded-t-xl"
              loading="lazy"
              onError={(e) => {
                // If image fails to load, hide it and show as external link instead
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        );
        
      default:
        return null;
    }
  };
  
  const renderExternalLink = () => {
    // Only show external link if it's not an image or if the image failed to load
    if (!story.image_url || mediaType !== 'external') return null;
    
    return (
      <div className="mt-6 pt-6 border-t border-dark/10">
        <a
          href={story.image_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-primary hover:text-primary/80 transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
          <span>View External Content</span>
        </a>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Link to={`/bands/${story.band?.slug}`} className="block">
            <div className="w-12 h-12 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
              {story.band?.avatar_url ? (
                <img
                  src={story.band.avatar_url}
                  alt={story.band.name}
                  className="w-full h-full object-cover"
                 loading="lazy" />
              ) : (
                <Users className="w-4 h-4 text-light/60" />
              )}
            </div>
          </Link>
          <div>
            <Link 
              to={`/bands/${story.band?.slug}`}
              className="font-medium text-light hover:text-light/80 transition-colors"
            >
              {story.band?.name}
            </Link>
            <div className="text-sm text-light/60">
              {format(new Date(story.created_at), 'PPP', { locale })}
            </div>
          </div>
        </div>
      </div>

      {renderMediaContent()}
      
      <div className={`bg-white p-6 overflow-y-auto ${story.image_url ? 'rounded-b-xl' : 'rounded-xl'}`}>
        <h2 className="font-tilt-warp text-3xl text-dark mb-3">{story.title}</h2>
        <div className="prose prose-lg max-w-none text-dark whitespace-pre-line">
          {story.content}
        </div>
        
        {renderExternalLink()}
        
        <div className="mt-6 pt-6 border-t border-dark/10">
          <StoryLikeButton 
            storyId={story.id}
            initialLikes={story.likes_count}
            className="!text-primary"
            showText={true}
          />
        </div>
      </div>

      <div className="mt-6 text-sm text-light/60">
        {t('common.postedBy')} {story.author.artist_name || story.author.full_name}
      </div>
    </Modal>
  );
}